<template>
  <div
    :class="[
      siteSection === SITE_SECTION.PUBLIC
        ? 'overflow-x-hidden bg-white text-home-gray-800'
        : 'bg-gray-100 text-dark',
    ]"
  >
    <div
      v-if="isLoadingApp"
      data-cy="app-loading"
      class="absolute top-0 z-10000 flex h-full w-full justify-center bg-white"
    >
      <img class="block w-6 lg:hidden" src="@/assets/img/icon.svg" alt="" />
      <img
        class="hidden w-1/6 animate-ping-logo lg:block"
        src="@/assets/img/logo.svg"
        alt=""
      />
    </div>
    <div>
      <div
        id="backdrop"
        :class="{
          'fixed z-10 h-full w-full bg-white opacity-90': backdrop,
        }"
      ></div>

      <div
        v-if="
          !(mobileLandscape && calendarPage) &&
          !(breakpoint >= 4 && siteSection === SITE_SECTION.ADMIN)
        "
        class="fixed top-0 z-100 w-full bg-white shadow"
      >
        <Navbar />
      </div>
      <div
        data-cy="alerts"
        class="fixed z-10000 w-full px-4 lg:left-1/4 lg:w-1/2 lg:px-0"
      >
        <div v-for="alert in alerts" :key="alert.key" class="mt-3">
          <alert
            :id="alert.key"
            :variant="alert.type"
            :duration="alert.countdown"
            :dismissible="true"
            @dismissed="clearAlert(alert.key)"
          >
            <div v-html="alert.message"></div>
          </alert>
        </div>
      </div>

      <section
        class="min-h-screen-navbar"
        :class="{
          'h-full xl:mt-0': siteSection === SITE_SECTION.ADMIN,
          'mt-16': !(mobileLandscape && calendarPage),
        }"
      >
        <div class="relative xl:flex">
          <div
            v-if="isStaffOrMore && siteSection === SITE_SECTION.ADMIN"
            class="overflofw-hidden z-0 hidden h-screen flex-none scrollbar-thin bg-white transition-all duration-1000 xl:sticky xl:top-0 xl:flex"
            :class="[reducedSidebar ? 'w-16' : 'w-80']"
          >
            <sidebar :reduced="reducedSidebar"></sidebar>
          </div>
          <div
            class="absolute top-18 h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-primary-100 text-primary-900 shadow-lg transition-all duration-1000"
            :class="[
              reducedSidebar
                ? 'left-[calc(4rem-13px)]'
                : 'left-[calc(20rem-13px)]',
              siteSection === SITE_SECTION.ADMIN
                ? 'hidden xl:inline-flex'
                : 'hidden',
            ]"
            data-cy="sidebar-toggle"
            @click="reduceSidebar()"
          >
            <i
              class="fas fa-chevron-right transform transition-all duration-1000"
              :class="[reducedSidebar ? 'rotate-0' : 'mr-0.5 rotate-180']"
            ></i>
          </div>
          <section class="xl:grow">
            <loading-indicator v-if="loadingComponent"></loading-indicator>
            <div v-show="!loadingComponent">
              <router-view :key="locale" />
            </div>
          </section>
        </div>
      </section>

      <div>
        <app-footer v-if="siteSection === SITE_SECTION.PUBLIC"></app-footer>
      </div>

      <pin-setup-modal
        v-if="isStaffOrMore"
        :key="loggedInUserId ?? 0"
        prompt
      ></pin-setup-modal>
    </div>
  </div>
</template>

<script setup lang="ts">
import PinSetupModal from '@/components/auth/PinSetupModal.vue';
import Sidebar from '@/components/Sidebar.vue';
import Alert from '@/components/shared/Alert.vue';
import LoadingIndicator from '@/components/shared/LoadingIndicator.vue';
import { useAppLoad } from '@/composables/app-load';
import { useSpaceGuard } from '@/composables/space-guard';
import { useCalendarStore } from '@/store/calendar-store';
import { useClosedDatesStore } from '@/store/closed-dates-store';
import { useEmployeesStore } from '@/store/employees-store';
import { useLocationsStore } from '@/store/locations-store';
import { useMachineModelsStore } from '@/store/machine-models-store';
import { useServicesStore } from '@/store/services-store';
import { useTimetablesStore } from '@/store/timetables-store';
import { SiteSection, useUiStore } from '@/store/ui-store';
import { useUsersStore } from '@/store/users-store';
import { useRoles } from '@/composables/roles';
import { useWorkplacesStore } from '@/store/workplaces-store';
import pick from 'lodash/pick';
import {
  computed,
  defineAsyncComponent,
  onMounted,
  onUnmounted,
  ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import i18n from '@/config/i18n';

const Navbar = defineAsyncComponent(() => import('@/components/Navbar.vue'));
const AppFooter = defineAsyncComponent(
  () => import('@/components/AppFooter.vue'),
);

const SITE_SECTION = SiteSection;
const SESSION_END_ACTIONS = ['logout', 'logoutDevice', 'parkSession'];

const uiStore = useUiStore();
const usersStore = useUsersStore();
const calendarStore = useCalendarStore();
const employeesStore = useEmployeesStore();
const timetablesStore = useTimetablesStore();
const route = useRoute();
const router = useRouter();
const { loadManagerData } = useAppLoad();

const unsubscribes: (() => void)[] = [];
const isLoadingApp = ref(true);

const alerts = computed(() => uiStore.alerts);
const siteSection = computed(() => uiStore.siteSection);
const breakpoint = computed(() => uiStore.breakpoint);
const backdrop = computed(() => uiStore.backdrop);
const mobileLandscape = computed(() => uiStore.mobileLandscape);
const reducedSidebar = computed(() => uiStore.reducedSidebar);
const loadingComponent = computed(() => uiStore.loadingComponent);
const { isStaffOrMore } = useRoles();
const loggedInUserId = computed(() => usersStore.loggedInUserId);
const locale = computed(() => i18n.global.locale.value);
const calendarPage = computed(() => route.path.includes('agenda'));

persistStoreInLocalStorage();
unsubscribes.push(useSpaceGuard());
loadApp();

onMounted(() => {
  const portraitMode = window.matchMedia('(orientation:portrait)');
  portraitMode.addEventListener('change', handleScreenOrientationChange);
  handleScreenOrientationChange();
  uiStore.setScreenWidth(window.screen.width);
  window.addEventListener('resize', handleScreenResize);
});

onUnmounted(() => {
  unsubscribes.forEach((unsubscribe) => unsubscribe());
});

async function loadApp() {
  try {
    await usersStore.setLanguage(
      usersStore.user.language || navigator.language.slice(0, 2),
    );
    await loadManagerData();
    checkVendorConfiguration();
    handleRedirectWhenLoggingOut();
  } finally {
    isLoadingApp.value = false;
  }
}

function setVh() {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
}

function handleScreenOrientationChange() {
  const portraitMode = window.matchMedia('(orientation:portrait)');
  setVh();
  uiStore.setScreenOrientation(portraitMode.matches ? 'portrait' : 'landscape');
}

function handleScreenResize() {
  setVh();
  uiStore.setScreenWidth(window.screen.width);
}

function persistOnActions(
  store: {
    $onAction: typeof calendarStore.$onAction;
  },
  storageKey: string,
  actions: string[],
  keys: string[],
) {
  unsubscribes.push(
    store.$onAction(({ name, after }) => {
      if (actions.includes(name)) {
        after(() => {
          localStorage.setItem(storageKey, JSON.stringify(pick(store, keys)));
        });
      }
    }),
  );
}

function persistStoreInLocalStorage() {
  if (localStorage.getItem('vuex') !== null) {
    localStorage.removeItem('vuex');
  }
  usersStore.$subscribe((mutation, state) => {
    localStorage.setItem('users', JSON.stringify(state));
  });
  persistOnActions(
    calendarStore,
    'calendar',
    [
      'setViewType',
      'setViewMode',
      'setDisplayType',
      'changeMonth',
      'setStartOfDay',
      'setZoom',
      'selectDate',
    ],
    [
      'viewType',
      'viewMode',
      'previousDisplayType',
      'displayType',
      'startOfDay',
      'zoom',
      'selectedDate',
    ],
  );
  persistOnActions(
    employeesStore,
    'employees',
    ['setCurrentEntity'],
    ['currentEntity'],
  );
  persistOnActions(
    timetablesStore,
    'timetables',
    ['setViewType'],
    ['viewType'],
  );
}

function checkVendorConfiguration() {
  if (!isStaffOrMore.value) {
    return;
  }
  [
    useLocationsStore(),
    useWorkplacesStore(),
    useServicesStore(),
    useMachineModelsStore(),
    employeesStore,
    useClosedDatesStore(),
  ].forEach((store) =>
    unsubscribes.push(
      store.$onAction(({ name, after }) => {
        if (name === 'findAll') {
          after(() => store.verifyConfiguration());
        }
      }),
    ),
  );
}

function handleRedirectWhenLoggingOut() {
  unsubscribes.push(
    usersStore.$onAction(({ name, after }) => {
      if (SESSION_END_ACTIONS.includes(name)) {
        after(() =>
          router.replace(
            usersStore.hasOtherDeviceSessions
              ? { name: 'DeviceSessions' }
              : { name: 'Login' },
          ),
        );
      }
    }),
  );
}

function clearAlert(key: number) {
  uiStore.clearAlert(key);
}

function reduceSidebar() {
  uiStore.reduceSidebar();
}
</script>
